<script lang="ts">
import { computed, defineComponent, inject, onActivated, onDeactivated, onMounted, PropType, ref, watch } from "vue";
import Steps from "@/components/Steps.vue";
import formValidierung from "@/businesslogic/formValidierung";
import Infotext from "@/components/Infotext.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import { NavvItem } from "@/model/NavItem";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import Zeile from "@/components/grid/Zeile.vue";
import Logger from "js-logger";
import Modal from "@/components/Modal.vue";
import MaklerHinweis from "@/views/vgv/modals/MaklerHinweis.vue";
import storeUtils from "@/businesslogic/utils/storeUtils";
import dataUtils from "@/businesslogic/utils/dataUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "GebaeudeausstattungVGV",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    ValidierungsFehler,
    Steps,
    Infotext,
    Zeile,
    Zelle,
    TitelZelle,
    Modal,
    MaklerHinweis,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
    isMakler: Boolean,
  },
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const feldBlurFocus: any = inject("feldBlurFocus");
    const istAktiv = ref(false);
    const { photovoltaikAlterVersicherbar, pvLeistungVersicherbar } = storeUtils();
    const { eq } = dataUtils();

    const photovoltaikInfoText =
      "Eine umfangreiche Absicherung, wie z.B. der Ertragsausfall oder Diebstahl der Paneele, ist möglich, " +
      "sofern Ihre Photovoltaikanlage folgende Kriterien erfüllt:<br />" +
      "1) max. 10 Jahre alt,<br />" +
      "2) max. 30 kWp und<br />" +
      "3) Installation oder Abnahme durch Fachfirma<br />" +
      "4) Installation auf oder am versicherten Haupt- oder Nebengebäude<br />" +
      "<u>Wichtig:</u> Gegen Schäden durch Feuer, Sturm und Hagel ist die PV-Anlage generell mitversichert.";
    const fehler = ref({} as any);
    const buttonText = ref("Weiter zur Tarifauswahl");
    const submitDeaktiviert = ref(false);

    const pvDetailfragenAnzeigenUndPruefen = computed(() => {
      return piniaStore.state.gebaeudedaten.photovoltaikVorhanden;
    });

    const baujahrMin = computed(() => {
      return piniaStore.state.gebaeudedaten.baujahr;
    });
    const baujahrMax = new Date().getFullYear() + 1;
    const baujahrFehlerText = computed(() => {
      return "Bitte geben Sie das vierstellige Baujahr (" + baujahrMin.value + " - " + baujahrMax + ") ein.";
    });

    const { erzwingeGanzzahl } = formValidierung();

    const zeigeHinweisPvAnlageBaujahr = computed(() => {
      return (
        piniaStore.state.isMakler &&
        piniaStore.state.gebaeudedaten.photovoltaikBaujahr !== null &&
        !photovoltaikAlterVersicherbar(piniaStore.state)
      );
    });

    const zeigeHinweisPvAnlageLeistung = computed(() => {
      return (
        piniaStore.state.isMakler &&
        piniaStore.state.gebaeudedaten.photovoltaikLeistung !== null &&
        !pvLeistungVersicherbar(piniaStore.state)
      );
    });

    const showables = ref({
      toggleMaklerHinweis: false,
    });

    watch(
      () => piniaStore.state.gebaeudedaten.nebengebaeudeVorhanden,
      (neu) => {
        Logger.debug("nebengebäude neu ", neu);
        if (neu === true) {
          submitDeaktiviert.value = true;
          toggleMaklerHinweis();
        } else {
          submitDeaktiviert.value = false;
        }
      },
    );
    const textAenderungMakler = () => {
      if (piniaStore.state.isMakler) {
        buttonText.value = "Weiter zum Vorschlag";
      }
    };

    const eingabenSindValide = async () => {
      const { validiereForm, getFormFehlerAnzahl, getFormFehler } = formValidierung();

      await validiereForm();

      fehler.value = getFormFehler();
      Logger.debug("Fehler", fehler.value, getFormFehlerAnzahl());
      return getFormFehlerAnzahl() === 0;
    };

    const nextPage = () => {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          piniaStore.state.neuberechnen = true;
        }
      });
    };

    const toggleMaklerHinweis = () => {
      showables.value.toggleMaklerHinweis = !showables.value.toggleMaklerHinweis;
    };

    const isEfhZfh = computed(() => {
      return (
        piniaStore.state.gebaeudedaten.gebaeudesubtyp !== "mfh" ||
        (piniaStore.state.gebaeudedaten.gebaeudesubtyp === "mfh" &&
          eq(piniaStore.state.gebaeudedaten.anzahlWohnungen, 2))
      );
    });

    onMounted(() => {
      textAenderungMakler();
      if (
        ((piniaStore.state.gebaeudedaten.anzahlGaragen != null && piniaStore.state.gebaeudedaten.anzahlGaragen > 0) ||
          (piniaStore.state.gebaeudedaten.anzahlCarports != null &&
            piniaStore.state.gebaeudedaten.anzahlCarports > 0)) &&
        piniaStore.state.gebaeudedaten.parkplaetzeVorhanden != true
      ) {
        piniaStore.state.gebaeudedaten.parkplaetzeVorhanden = true;
      }
    });

    watch(
      () => [piniaStore.state.berechnungDurchgefuehrt],
      () => {
        if (piniaStore.state.berechnungDurchgefuehrt && istAktiv.value) {
          piniaStore.state.berechnungDurchgefuehrt = false;
          emit("next-page", { pageIndex: 1 });
        }
      },
    );

    onActivated(() => {
      istAktiv.value = true;
      piniaStore.state.berechnungDurchgefuehrt = false;
    });

    onDeactivated(() => {
      istAktiv.value = false;
    });

    return {
      nextPage,
      feldBlurFocus,
      wechsleSichtbarkeit,
      istSichtbar,
      piniaStore,
      fehler,
      props,
      buttonText,
      photovoltaikInfoText,
      baujahrFehlerText,
      baujahrMin,
      baujahrMax,
      erzwingeGanzzahl,
      pvDetailfragenAnzeigenUndPruefen,
      zeigeHinweisPvAnlageBaujahr,
      zeigeHinweisPvAnlageLeistung,
      toggleMaklerHinweis,
      showables,
      submitDeaktiviert,
      isEfhZfh,
    };
  },
});
</script>

<template>
  <Steps v-bind:items="navItems" :active="1" @goto-page="$emit('goto-page', $event)"></Steps>

  <section>
    <zeile>
      <titel-zelle :zeige-info="true" info-box-id="dachVarianteInfo">
        <strong>Dachart und -ausbau</strong>
      </titel-zelle>
      <zelle class="iconRadioDachausbau">
        <input
          id="dachAusgebaut"
          name="dachvariante"
          class="no-outline"
          v-model="piniaStore.state.gebaeudedaten.dachvariante"
          type="radio"
          value="3"
          required
          aria-required="true"
          tabindex="0"
          aria-label="ausgebautes Dach"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl."
          data-cy="LoftConversion"
        />
        <label for="dachAusgebaut" class="radiolabelDachausbau"></label>
        <input
          id="dachNichtAusgebaut"
          name="dachvariante"
          class="no-outline"
          v-model="piniaStore.state.gebaeudedaten.dachvariante"
          type="radio"
          value="2"
          aria-label="Dach nicht ausgebaut"
          autofocus
          data-cy="LoftConversionNO"
        />
        <label for="dachNichtAusgebaut" class="radiolabelDachausbau"></label>
        <input
          id="flachdach"
          name="dachvariante"
          class="no-outline"
          v-model="piniaStore.state.gebaeudedaten.dachvariante"
          type="radio"
          value="1"
          aria-label="Flachdach"
          data-cy="FlatRoof"
        />
        <label for="flachdach" class="radiolabelDachausbau"></label>

        <validierungs-fehler :fehler-text="fehler.dachvariante" :reduzierter-abstand="true" />
        <Infotext
          name="dachVarianteInfo"
          css-klassen="o-grid__cell&#45;&#45;no-space"
          text="Als Vollgeschoss gelten alle zu Wohnzwecken ausgebauten Etagen. Ausgenommen sind Geschosse mit Schrägen (Dachausbau) sowie Keller."
        >
        </Infotext>
      </zelle>
    </zeile>
    <zeile>
      <titel-zelle :zeige-info="true" info-box-id="anzahlVollgeschosseInfo">
        <strong>Anzahl Vollgeschosse</strong>
      </titel-zelle>
      <zelle :breite="4">
        <select
          id="anzahlVollgeschosse"
          v-model.number="piniaStore.state.gebaeudedaten.anzahlVollgeschosse"
          class="c-form-control-select"
          data-cy="NumberOfFloors"
          aria-label="Anzahl Vollgeschosse"
          aria-required="true"
          aria-describedby="anzahlVollgeschosseInfo"
        >
          <option selected value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="4">4</option>
          <option value="5">5</option>
          <option value="6">6</option>
          <option value="7">7</option>
        </select>
        <infotext
          id="anzahlVollgeschosseInfo"
          name="anzahlVollgeschosseInfo"
          css-klassen="o-grid__cell--no-space"
          text="Als Vollgeschoss gelten alle zu Wohnzwecken ausgebauten Etagen.
            Ausgenommen sind Dachgeschosse mit Schrägen sowie Keller."
        >
        </infotext>
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.gebaeudedaten.gebaeudesubtyp === 'mfh'">
      <titel-zelle>
        <strong>Anzahl Wohneinheiten</strong>
      </titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="anzahlWohneinheiten"
          name="anzahlWohneinheiten"
          v-model.number="piniaStore.state.gebaeudedaten.anzahlWohnungen"
          class="c-form-control__input"
          placeholder="2-99"
          min="2"
          max="99"
          required
          aria-required="true"
          aria-label="Anzahl der Wohneinheiten"
          data-fehlermeldung="Bitte geben Sie die Anzahl der Wohneinheiten zwischen 2 und 99 ein."
          data-cy="AnzahlWohneinheiten"
        />
        <validierungs-fehler :fehler-text="fehler.anzahlWohneinheiten" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <zeile>
      <titel-zelle
        v-if="piniaStore.state.gebaeudedaten.gewerbeVorhanden === false"
        :zeige-info="true"
        info-box-id="wohnflaecheInfo"
      >
        <strong>Wohnfläche</strong> (ohne Keller) in m<sup>2</sup>
      </titel-zelle>
      <titel-zelle v-else :zeige-info="true" info-box-id="wohnflaecheInfo">
        <strong>Wohn- und Gewerbefläche</strong> (ohne Keller) in m<sup>2</sup>
      </titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="wohnflaeche"
          name="wohnflaeche"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.wohnflaeche"
          @keyup="feldBlurFocus($event, 900)"
          class="c-form-control__input"
          :min="piniaStore.state.isMakler ? 1 : 15"
          max="5000"
          required
          aria-required="true"
          aria-label="Wohn- und Gewerbefläche ohne Keller in Quadratmetern"
          :data-fehlermeldung="
            'Bitte geben Sie eine Fläche zwischen ' + (piniaStore.state.isMakler ? 1 : 15) + ' und 5.000 m² ein.'
          "
          data-cy="AreaLiving"
        />
        <validierungs-fehler :fehler-text="fehler.wohnflaeche" />
      </zelle>
      <zelle :breite="8" :offset="4">
        <infotext
          name="wohnflaecheInfo"
          css-klassen="o-grid__cell--no-space"
          text='Die Wohnfläche ist die Grundfläche aller Räume des Gebäudes
          einschließlich Hobbyräume. Nicht zu berücksichtigen sind Treppen,
          Balkone, Loggien und Terrassen sowie Keller-, Speicher- und
          Bodenräume, die nicht zu Wohn- oder Hobbyzwecken genutzt werden.<br />
          Alternativ akzeptieren wir die Angabe der Gesamtfläche entsprechend
          <ul style="padding-left: 0.9em;">
          <li>der Wohnflächenverordnung (WoFlV),</li>
          <li>der Nutzfläche gemäß DIN 277,</li>
          <li>
            dem Miet- oder Kaufvertrag, sofern dieser den aktuellen
            Ausbauzustand wiedergibt.
          </li>
          </ul>'
        >
        </infotext>
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.isMakler === true">
      <titel-zelle :zeige-info="true" info-box-id="nebengebaeudeInfo"> Nebengebäude >40 m² vorhanden?</titel-zelle>
      <zelle class="mkRadio">
        <input
          type="radio"
          id="nebengebaeudeNein"
          name="nebengebaeudeVorhanden"
          :value="false"
          v-model="piniaStore.state.gebaeudedaten.nebengebaeudeVorhanden"
          :required="piniaStore.state.isMakler"
          :aria-required="piniaStore.state.isMakler"
          aria-label="Nein, es ist kein Nebengebäude größer 40 Quadratmeter vorhanden"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl"
          data-cy="SideBuildingNo"
        />
        <label for="nebengebaeudeNein" class="radiolabel">Nein</label>
        <input
          type="radio"
          id="nebengebaeudeJa"
          name="nebengebaeudeVorhanden"
          :value="true"
          aria-label="Ja, es ist ein Nebengebäude größer 40 Quadratmeter vorhanden"
          v-model="piniaStore.state.gebaeudedaten.nebengebaeudeVorhanden"
          data-cy="SideBuilding"
        />
        <label for="nebengebaeudeJa" class="radiolabel">Ja</label>
        <validierungs-fehler :fehler-text="fehler.nebengebaeudeVorhanden" :reduzierter-abstand="true" />
        <infotext
          name="nebengebaeudeInfo"
          css-klassen="o-grid__cell--no-space"
          text="Kleinere Nebengebäude bis 40 m² sind im Rahmen geltender Entschädigungsgrenzen ohne Angabe mitversichert. Bitte beachten Sie hierzu die Leistungsübersichten der Produktlinien."
        ></infotext>
      </zelle>
    </zeile>
    <zeile>
      <titel-zelle><strong>Keller</strong> vorhanden?</titel-zelle>
      <zelle class="mkRadio">
        <input
          type="radio"
          id="kellerVorhandenNein"
          name="kellerVorhanden"
          v-model="piniaStore.state.gebaeudedaten.kellerVorhanden"
          :value="false"
          required
          aria-required="true"
          aria-label="Nein, es ist kein Keller vorhanden"
          data-fehlermeldung="Bitte wählen Sie die Art der Unterkellerung aus."
          data-cy="BasementNo"
        />
        <label for="kellerVorhandenNein" class="radiolabel">Nein</label>
        <input
          type="radio"
          id="kellerVorhandenJa"
          name="kellerVorhanden"
          v-model="piniaStore.state.gebaeudedaten.kellerVorhanden"
          :value="true"
          aria-label="Ja, es ist ein Keller vorhanden"
          data-cy="Basement"
        />
        <label for="kellerVorhandenJa" class="radiolabel">Ja</label>
        <validierungs-fehler :fehler-text="fehler.kellerVorhanden" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.gebaeudedaten.kellerVorhanden === true">
      <titel-zelle v-if="piniaStore.state.gebaeudedaten.gewerbeVorhanden === false">
        <strong>Kellerfläche</strong> bei Wohn- oder Hobbynutzung in m<sup>2</sup>
      </titel-zelle>
      <titel-zelle v-else>
        <strong>Kellerfläche</strong> bei Wohn-, Gewerbe- oder Hobbynutzung in m<sup>2</sup>
      </titel-zelle>

      <zelle>
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="wohnflaecheKeller"
          name="wohnflaecheKeller"
          v-model.number="piniaStore.state.gebaeudedaten.wohnflaecheKeller"
          class="c-form-control__input"
          placeholder="m²"
          min="0"
          :max="piniaStore.state.gebaeudedaten.wohnflaeche ? piniaStore.state.gebaeudedaten.wohnflaeche : 999"
          :required="piniaStore.state.gebaeudedaten.kellerVorhanden === true"
          :aria-required="piniaStore.state.gebaeudedaten.kellerVorhanden === true"
          aria-label="Kellerfläche bei Wohn-, Gewerbe- oder Hobbynutzung in Quadratmetern"
          :data-fehlermeldung="
            'Bitte geben Sie eine Kellerfläche zwischen 0 und ' +
            (piniaStore.state.gebaeudedaten.wohnflaeche ? piniaStore.state.gebaeudedaten.wohnflaeche : 999) +
            ' m² ein.'
          "
          data-cy="AreaLivingBasement"
        />
        <validierungs-fehler :fehler-text="fehler.wohnflaecheKeller" />
      </zelle>
    </zeile>
    <zeile>
      <titel-zelle><strong>Garagen oder Carports</strong> vorhanden?</titel-zelle>
      <zelle class="mkRadio">
        <input
          type="radio"
          id="parkplaetzeVorhandenNein"
          name="parkplaetzeVorhanden"
          :value="false"
          v-model="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden"
          required
          aria-required="true"
          aria-label="Nein, es sind keine Garagen oder Carports vorhanden"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl"
          data-cy="CarParkNo"
        />
        <label for="parkplaetzeVorhandenNein" class="radiolabel">Nein</label>
        <input
          type="radio"
          id="parkplaetzeVorhandenJa"
          :value="true"
          name="parkplaetzeVorhanden"
          v-model="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden"
          aria-label="Ja, es sind Garagen oder Carports vorhanden"
          data-cy="CarPark"
        />
        <label for="parkplaetzeVorhandenJa" class="radiolabel">Ja</label>
        <validierungs-fehler :fehler-text="fehler.parkplaetzeVorhanden" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden === true">
      <titel-zelle :zeige-info="true" info-box-id="garagenInfo">
        Anzahl der Stellplätze in <strong>Garagen</strong>
        {{ piniaStore.state.isMakler === true && isEfhZfh ? "außerhalb des Hauptgebäudes" : "" }}
      </titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="anzahlGaragen"
          name="anzahlGaragen"
          v-model.number="piniaStore.state.gebaeudedaten.anzahlGaragen"
          class="c-form-control__input"
          placeholder="0-99"
          min="0"
          max="99"
          :required="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden"
          :aria-required="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden"
          aria-label="Anzahl der Stellplätze in Garagen"
          data-fehlermeldung="Bitte geben Sie die Anzahl der Stellplätze zwischen 0 und 99 ein."
          data-cy="GarageSpace"
        />
        <validierungs-fehler :fehler-text="fehler.anzahlGaragen" />
        <infotext
          name="garagenInfo"
          css-klassen="o-grid__cell--no-space"
          text=" Zu Garagen zählen wir:<br />- freistehende Garagen<br />
            - angebaute Garagen<br />- Tiefgaragen"
        ></infotext>
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden === true">
      <titel-zelle> Anzahl der Stellplätze in <strong>Carports</strong></titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="anzahlCarports"
          name="anzahlCarports"
          v-model.number="piniaStore.state.gebaeudedaten.anzahlCarports"
          class="c-form-control__input"
          placeholder="0-99"
          min="0"
          max="99"
          :required="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden"
          :aria-required="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden"
          aria-label="Anzahl der Stellplätze in Carports"
          data-fehlermeldung="Bitte geben Sie die Anzahl der Stellplätze zwischen 0 und 99 ein."
          data-cy="CarportSpace"
        />
        <validierungs-fehler :fehler-text="fehler.anzahlCarports" />
      </zelle>
    </zeile>
    <zeile>
      <titel-zelle :zeige-info="true" info-box-id="photovoltaikInfo">
        <strong>Photovoltaikanlage</strong> auf dem Gebäude?
      </titel-zelle>
      <zelle class="mkRadio">
        <input
          type="radio"
          id="photovoltaikNein"
          name="photovoltaikVorhanden"
          v-model="piniaStore.state.gebaeudedaten.photovoltaikVorhanden"
          :value="false"
          required
          aria-required="true"
          aria-label="Nein, es befindet sich keine Photovoltaikanlage auf dem Gebäude"
          data-fehlermeldung="Bitte wählen Sie aus, ob eine PV-Anlage existiert."
          data-cy="PhotovoltaicNo"
        />
        <label for="photovoltaikNein" class="radiolabel">Nein</label>
        <input
          type="radio"
          id="photovoltaikJa"
          :value="true"
          name="photovoltaikVorhanden"
          v-model="piniaStore.state.gebaeudedaten.photovoltaikVorhanden"
          aria-label="Ja, es befindet sich eine Photovoltaikanlage auf dem Gebäude"
          data-cy="Photovoltaic"
        />
        <label for="photovoltaikJa" class="radiolabel">Ja</label>
        <validierungs-fehler :fehler-text="fehler.photovoltaikVorhanden" :reduzierter-abstand="true" />
        <infotext name="photovoltaikInfo" css-klassen="o-grid__cell--no-space" :text="photovoltaikInfoText"></infotext>
      </zelle>
    </zeile>
    <zeile v-if="pvDetailfragenAnzeigenUndPruefen">
      <titel-zelle><strong>Baujahr</strong> der Photovoltaikanlage</titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="photovoltaikBaujahr"
          name="photovoltaikBaujahr"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.photovoltaikBaujahr"
          class="c-form-control__input"
          placeholder="JJJJ"
          :min="baujahrMin"
          :max="baujahrMax"
          :required="pvDetailfragenAnzeigenUndPruefen"
          :aria-required="pvDetailfragenAnzeigenUndPruefen"
          aria-label="Baujahr der Photovoltaikanlage"
          :data-fehlermeldung="baujahrFehlerText"
          data-cy="PhotovoltaicYear"
        />
        <div v-if="zeigeHinweisPvAnlageBaujahr">
          Der Einschluss einer zusätzlichen Absicherung der Photovoltaikanlage ist aufgrund der fehlenden
          Voraussetzungen des Alters der Anlage nicht möglich.<br />
          Hinweis: Gegen Schäden durch Feuer, Sturm und Hagel ist die PV-Anlage generell mitversichert.
        </div>
        <validierungs-fehler :fehler-text="fehler.photovoltaikBaujahr" />
      </zelle>
    </zeile>
    <zeile v-if="pvDetailfragenAnzeigenUndPruefen">
      <titel-zelle><strong>Leistung</strong> der Photovoltaikanlage in kWp (Spitzenleistung)</titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          id="photovoltaikLeistung"
          name="photovoltaikLeistung"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.photovoltaikLeistung"
          @keydown="erzwingeGanzzahl"
          class="c-form-control__input"
          placeholder="kWp"
          min="1"
          max="99"
          :required="pvDetailfragenAnzeigenUndPruefen"
          :aria-required="pvDetailfragenAnzeigenUndPruefen"
          aria-label="Leistung der Photovoltaikanlage in kWp (Spitzenleistung)"
          data-fehlermeldung="Bitte geben Sie die Leistung in ganzen kWp an (1-99)."
          data-cy="PhotovoltaicPower"
        />
        <div v-if="zeigeHinweisPvAnlageLeistung">
          Der Einschluss einer zusätzlichen Absicherung der Photovoltaikanlage ist aufgrund der fehlenden
          Voraussetzungen der Leistung der Anlage nicht möglich.<br />
          Hinweis: Gegen Schäden durch Feuer, Sturm und Hagel ist die PV-Anlage generell mitversichert.
        </div>
        <validierungs-fehler :fehler-text="fehler.photovoltaikLeistung" />
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.isMakler">
      <titel-zelle>Schwimmbad vorhanden</titel-zelle>
      <zelle class="mkRadio">
        <input
          type="radio"
          id="schwimmbadVorhandenNein"
          :value="false"
          name="schwimmbadVorhanden"
          v-model="piniaStore.state.gebaeudedaten.schwimmbadVorhanden"
          aria-label="Nein, es ist kein Schwimmbad vorhanden"
          data-cy="PoolNo"
        />
        <label for="schwimmbadVorhandenNein" class="radiolabel">Nein</label>
        <input
          type="radio"
          id="schwimmbadVorhandenJa"
          :value="true"
          name="schwimmbadVorhanden"
          v-model="piniaStore.state.gebaeudedaten.schwimmbadVorhanden"
          aria-label="Ja, es ist ein Schwimmbad vorhanden"
          data-cy="Pool"
        />
        <label for="schwimmbadVorhandenJa" class="radiolabel">Ja</label>
      </zelle>
    </zeile>
    <zeile>
      <zelle :breite="3" :offset="4">
        <button
          class="c-button c-button--full-width u-mar-t-1 u-mar-b-2 mobil-mehrAbstand"
          @click="nextPage"
          :disabled="submitDeaktiviert"
        >
          {{ buttonText }}
        </button>
      </zelle>
    </zeile>
    <modal
      v-if="piniaStore.state.isMakler && showables.toggleMaklerHinweis"
      @closeModal="toggleMaklerHinweis"
      titel="Nebengebäude"
    >
      <template v-slot:inhalt>
        <makler-hinweis />
      </template>
    </modal>
  </section>
</template>

<style scoped></style>
